import { z } from "zod";
import {
  CONTACT_NAME_MAX,
  NOTES_MAX,
  PHONE_MAX,
  PHONE_REGEX,
  RELATIONSHIP_MAX,
} from "@shared/constants/validation.js";

/**
 * Matches children.photoUrl handling: a small data-URI produced by the
 * browser-side downscaler (client/src/lib/img.ts), never a remote URL. The cap
 * keeps a single pickup photo well inside the global 64KB request body limit.
 */
const PHOTO_DATA_URI_MAX = 60_000;

const nullableTrimmedString = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v && v.length > 0 ? v : null));

const updateNullableString = (max: number) =>
  z.string().trim().max(max).nullable().optional();

const phoneField = z
  .string()
  .trim()
  .max(PHONE_MAX)
  .regex(PHONE_REGEX, "Enter a valid phone number")
  .nullish()
  .transform((v) => (v && v.length > 0 ? v : null));

const photoField = z
  .string()
  .trim()
  .max(PHOTO_DATA_URI_MAX, "Photo is too large")
  .nullish()
  .transform((v) => (v && v.length > 0 ? v : null));

export const createAuthorizedPickupSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Name is required")
    .max(CONTACT_NAME_MAX, "Name is too long"),
  relationship: nullableTrimmedString(RELATIONSHIP_MAX),
  phone: phoneField,
  photoUrl: photoField,
  isActive: z.boolean().default(true),
  notes: nullableTrimmedString(NOTES_MAX),
});

export const updateAuthorizedPickupSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(CONTACT_NAME_MAX).optional(),
  relationship: updateNullableString(RELATIONSHIP_MAX),
  phone: phoneField,
  photoUrl: photoField,
  isActive: z.boolean().optional(),
  notes: updateNullableString(NOTES_MAX),
});

export type CreateAuthorizedPickupInput = z.infer<typeof createAuthorizedPickupSchema>;
export type UpdateAuthorizedPickupInput = z.infer<typeof updateAuthorizedPickupSchema>;
